import React, { useEffect, useRef } from "react";
import "./styles.scss";
import { GetProductivityReportData } from "./tenantApiCalls";
import { iDropdown, reportFilter } from ".";
import { productivityReport, summaryReport } from "./interfaces";
import { ConvertSecondsToHHMMSS, FormatFullDate } from "../../Logic/DateTimeFormatters";
import { getSessionValue } from "../../utilities/localStore/calls";
import Processing from "../Generic/Processing";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { ChartOptions, ChartData } from "chart.js";

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Title, Tooltip, Legend);
type GraphDataObject = {
    xAxisLabels: number[];
    Completed: number[];
    HasNew: number[];
    Pend: number[];
    Received: number[];
};

const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
        x: {
            grid: {
                display: false,
            },
            title: {
                display: true,
                text: "Hour of Day",
                font: {
                    size: 8,
                    weight: "bold",
                },
            },
        },
        y: {
            grid: {
                display: true,
            },
            ticks: {
                stepSize: 1,
            },
            min: 0,
            title: {
                display: true,
                text: "Number of Enquiries",
                font: {
                    size: 8,
                    weight: "bold",
                },
            },
        },
    },

    plugins: {
        legend: {
            position: "right",
            labels: {
                boxWidth: 10, // default is 40, reduce to make rectangles smaller
                font: {
                    size: 9, // reduce font size for a more compact legend
                },
                padding: 8, // optional: reduce spacing between legend items
            },
        },
        title: { display: true, text: "Summary of Wizard Entry Status" },
    },
};
function UserProductivityReport(props: summaryReport) {
    const [data, setData] = React.useState<productivityReport | undefined>(undefined);
    const reportTable = useRef<HTMLTableElement>(null);
    const [localProps, setLocalProps] = React.useState(() => ({ ...props }));
    const [GraphData, setGraphData] = React.useState<GraphDataObject>({
        xAxisLabels: [],
        Pend: [],
        Received: [],
        Completed: [],
        HasNew: [],
    });

    const [reportData, setReportData] = React.useState<{
        dateFrom: string;
        dateTo: string;
        user: string;
        mailbox: string;
        department: string;
        type: string;
        displayProductionCol: boolean;
    }>({
        dateFrom: "",
        dateTo: "",
        user: "",
        mailbox: "",
        department: "",
        type: "",
        displayProductionCol: true,
    });
    var userData: iDropdown[] | null = getSessionValue("users");
    var departmentData: iDropdown[] | null = getSessionValue("departments");
    var mailboxData: iDropdown[] | null = getSessionValue("mailboxes");
    useEffect(() => {
        (async () => {
            if (props.generateReport) {
                const result = await GetProductivityReportData(props);
                if (result.isOk && result.data) {
                    let data = result.data;
                    data.productionReportData.sort((a, b) => {
                        const aLabel = a?.rowLabel ?? "";
                        const bLabel = b?.rowLabel ?? "";

                        const isATeamTotal = aLabel === "Total";
                        const isBTeamTotal = bLabel === "Total";

                        if (isATeamTotal && !isBTeamTotal) return 1; // a goes after b
                        if (!isATeamTotal && isBTeamTotal) return -1; // b goes after a

                        return aLabel.localeCompare(bLabel, undefined, {
                            numeric: true,
                            sensitivity: "base",
                        });
                    });

                    setData(data);
                }
                const diffInMs =
                    new Date(new Date(props.dateTo.toString()).toDateString()).valueOf() -
                    new Date(new Date(props.dateFrom.toString()).toDateString()).valueOf();
                const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

                setReportData({
                    dateFrom: FormatFullDate(props.dateFrom.toString()),
                    dateTo: FormatFullDate(props.dateTo.toString()),
                    user:
                        props.users.length == 1
                            ? userData?.find(x => x.value == props.users[0])?.title
                                ? userData!.find(x => x.value == props.users[0])!.title
                                : "All Users"
                            : "All Users",
                    department:
                        props.serviceCentres.length == 1
                            ? departmentData?.find(x => x.value == props.serviceCentres[0])?.title
                                ? departmentData!.find(x => x.value == props.serviceCentres[0])!.title
                                : "All Departments"
                            : "All Departments",
                    mailbox:
                        props.mailboxes.length == 1
                            ? mailboxData?.find(x => x.value == props.mailboxes[0])?.title
                                ? mailboxData!.find(x => x.value == props.mailboxes[0])!.title
                                : "All Mailboxes"
                            : "All Mailboxes",
                    type: props.type == 1 ? "Summary" : props.type == 2 ? " By Hour" : "By Day",
                    displayProductionCol: Math.trunc(diffInDays) == 0 ? true : false,
                });
            }
        })();
    }, [props.generateReport]);

    React.useEffect(() => {
        setLocalProps({ ...props });
    }, [props]);

    const downloadReport = () => {
        if (reportTable.current) {
            // Create a workbook from the HTML table
            const workbook = XLSX.utils.table_to_book(reportTable.current);

            // Write the workbook to a binary string
            const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

            // Create a Blob and save the file
            const data = new Blob([excelBuffer], { type: "application/octet-stream" });
            const fileName = "Productivity_Report_" + Date.now();
            saveAs(data, `${fileName}.xlsx`);
        }
    };
    React.useEffect(() => {
        if (props.generateReport && data !== undefined) {
            updateGraphData();
        }
    }, [props.generateReport, data]);

    async function updateGraphData() {
        const updatedGraphData = {
            xAxisLabels: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24],
            HasNew: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            Received: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            Completed: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            Pend: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        };
        let maxIndex = 22;
        let minIndex = 5;

        if (data?.productionChartData)
            data?.productionChartData.forEach(row => {
                const index = updatedGraphData.xAxisLabels.indexOf(Number(row.hour));
                if (index !== -1) {
                    updatedGraphData.Completed[index] = row.completedCount ?? 0;
                    updatedGraphData.HasNew[index] = row.hasNewCount ?? 0;
                    updatedGraphData.Pend[index] = row.pendingCount ?? 0;
                    updatedGraphData.Received[index] = row.receivedCount ?? 0;
                    if (index > maxIndex) maxIndex = index;
                    if (index < minIndex) minIndex = index;
                }
            });
        let updatedGraphData1 = trimEmptyEnd(updatedGraphData, maxIndex);
        let updatedGraphData2 = trimEmptyStart(updatedGraphData1, minIndex);
        setGraphData(updatedGraphData2);
    }
    function trimEmptyEnd(data: any, max: number) {
        let trimIndex = max;

        for (let i = max + 1; i < 24; i++) {
            const isEmpty =
                (data.HasNew[i] ?? 0) === 0 &&
                (data.Received[i] ?? 0) === 0 &&
                (data.Completed[i] ?? 0) === 0 &&
                (data.Pend[i] ?? 0) === 0;

            if (isEmpty) {
                trimIndex = i - 1;
            } else {
                break;
            }
        }

        for (const key of Object.keys(data)) {
            if (Array.isArray(data[key])) {
                data[key] = data[key].slice(0, trimIndex);
            }
        }
        return data;
    }
    function trimEmptyStart(data: any, min: number) {
        let startIndex = 0;

        for (let i = 0; i <= min; i++) {
            const isEmpty =
                (data.HasNew[i] ?? 0) === 0 &&
                (data.Received[i] ?? 0) === 0 &&
                (data.Completed[i] ?? 0) === 0 &&
                (data.Pend[i] ?? 0) === 0;

            if (isEmpty) {
                startIndex = i + 1;
            } else {
                break;
            }
        }

        Object.keys(data).forEach(key => {
            if (Array.isArray(data[key])) {
                data[key] = data[key].slice(startIndex);
            }
        });

        return data;
    }

    function getCSSVar(name: any) {
        return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    }

    const chartData: ChartData<"line"> = {
        labels: GraphData.xAxisLabels,
        datasets: [
            {
                label: "Completed",
                data: GraphData.Completed,
                borderColor: getCSSVar("--completed-border"),
                backgroundColor: getCSSVar("--completed-backColor"),
                tension: 0.3,
                fill: true,
                animation: false,
            },
            {
                label: "HasNew",
                data: GraphData.HasNew,
                borderColor: getCSSVar("--hasNew-border"),
                backgroundColor: getCSSVar("--hasNew-backColor"),
                tension: 0.3,
                fill: true,
                animation: false,
            },

            {
                label: "Received",
                data: GraphData.Received,
                borderColor: getCSSVar("--received-border"),
                backgroundColor: getCSSVar("--received-backColor"),
                tension: 0.3,
                fill: true,
                animation: false,
            },
            {
                label: "Pend",
                data: GraphData.Pend,
                borderColor: getCSSVar("--pend-border"),
                backgroundColor: getCSSVar("--pend-backColor"),
                tension: 0.3,
                fill: true,
                animation: false,
            },
        ],
    };
    return (
        <>
            {data == undefined && (
                <Processing>
                    {" "}
                    <p>
                        Getting Report Data
                        <br />
                        Please wait...
                    </p>
                </Processing>
            )}
            {data != undefined && (
                <div className="reportPadding">
                    <div>
                        <div style={{ textAlign: "right", paddingBottom: "7px" }}>
                            <div style={{ float: "left" }}></div>
                            <button
                                className="g-btn g-btn-action"
                                onClick={downloadReport}
                            >
                                {" "}
                                Download Report
                            </button>{" "}
                        </div>
                        <div style={{ height: "300px", width: "100%" }}>
                            <Line
                                options={options}
                                data={chartData}
                            />
                        </div>
                        <table
                            className="reportTable"
                            ref={reportTable}
                        >
                            <thead>
                                <tr>
                                    <th
                                        style={{ width: "50%", fontSize: "14px", border: "0px" }}
                                        colSpan={6}
                                    >
                                        User Wizard Productivity Report
                                    </th>
                                    <th
                                        colSpan={reportData.displayProductionCol ? 7 : 6}
                                        rowSpan={2}
                                        className="right"
                                    >
                                        From: {reportData.dateFrom}
                                        <br />
                                        To: {reportData.dateTo}
                                    </th>
                                </tr>
                                <tr>
                                    <th
                                        style={{ border: "0px" }}
                                        colSpan={6}
                                    >
                                        User: {reportData.user} , &nbsp; Department: &nbsp;
                                        {reportData.department}
                                        &nbsp;, &nbsp; Mailboxes: &nbsp;
                                        {reportData.mailbox}
                                        &nbsp;, &nbsp; Type: &nbsp;
                                        {reportData.type}
                                    </th>
                                </tr>
                                <tr className="greyRow">
                                    <td
                                        rowSpan={2}
                                        colSpan={2}
                                        className="center"
                                        style={{ borderLeft: "solid 1px #aaa" }}
                                    >
                                        User
                                    </td>
                                    <td
                                        colSpan={4}
                                        className="center"
                                    >
                                        Pending
                                    </td>
                                    <td
                                        colSpan={4}
                                        className="center"
                                    >
                                        Completed
                                    </td>
                                    <td
                                        rowSpan={2}
                                        className="center"
                                    >
                                        Total
                                    </td>
                                    {reportData.displayProductionCol && (
                                        <td
                                            className="center"
                                            rowSpan={2}
                                            colSpan={2}
                                        >
                                            (min) <br></br> production
                                        </td>
                                    )}
                                </tr>
                                <tr className="greyRow">
                                    <td className="center">
                                        General
                                        <br />
                                        {data.mailboxProductionTimeData &&
                                            ConvertSecondsToHHMMSS(
                                                data.mailboxProductionTimeData.find(
                                                    x => x.statusName == "Pending - General",
                                                )?.productionTime ?? 0,
                                            )}
                                    </td>
                                    <td className="center">
                                        RIF
                                        <br />
                                        {data.mailboxProductionTimeData &&
                                            ConvertSecondsToHHMMSS(
                                                data.mailboxProductionTimeData.find(
                                                    x => x.statusName == "Pending - Request Internal Feedback",
                                                )?.productionTime ?? 0,
                                            )}
                                    </td>
                                    <td className="center">
                                        Corresponded
                                        <br />
                                        {data.mailboxProductionTimeData &&
                                            ConvertSecondsToHHMMSS(
                                                data.mailboxProductionTimeData.find(
                                                    x => x.statusName == "Pending - Correspond with Enquirer",
                                                )?.productionTime ?? 0,
                                            )}
                                    </td>
                                    <td className="center">
                                        Escalated
                                        <br />
                                        {data.mailboxProductionTimeData &&
                                            ConvertSecondsToHHMMSS(
                                                data.mailboxProductionTimeData.find(
                                                    x => x.statusName == "Pending - Escalated",
                                                )?.productionTime ?? 0,
                                            )}
                                    </td>
                                    <td className="center">
                                        Responded
                                        <br />
                                        {data.mailboxProductionTimeData &&
                                            ConvertSecondsToHHMMSS(
                                                data.mailboxProductionTimeData.find(
                                                    x => x.statusName == "Completed - Responded",
                                                )?.productionTime ?? 0,
                                            )}
                                    </td>
                                    <td className="center">
                                        No Response
                                        <br />
                                        {data.mailboxProductionTimeData &&
                                            ConvertSecondsToHHMMSS(
                                                data.mailboxProductionTimeData.find(
                                                    x => x.statusName == "Completed - No response",
                                                )?.productionTime ?? 0,
                                            )}
                                    </td>
                                    <td className="center">
                                        Delegate
                                        <br />
                                        {data.mailboxProductionTimeData &&
                                            ConvertSecondsToHHMMSS(
                                                data.mailboxProductionTimeData.find(
                                                    x => x.statusName == "Completed - Forwarded",
                                                )?.productionTime ?? 0,
                                            )}
                                    </td>
                                    <td className="center">
                                        Reassign
                                        <br />
                                        {data.mailboxProductionTimeData &&
                                            ConvertSecondsToHHMMSS(
                                                data.mailboxProductionTimeData.find(
                                                    x => x.statusName == "Completed - Reassigned",
                                                )?.productionTime ?? 0,
                                            )}
                                    </td>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    //user start
                                }

                                {data.productionReportData.map((x, index) => {
                                    const isTotalRow = x.rowLabel == "Total" ? true : false;
                                    return (
                                        <>
                                            <tr
                                                style={{
                                                    background: index % 2 === 0 ? "#fff" : "#f3f3f3",
                                                    borderTop: isTotalRow ? "solid 2px #666" : "",
                                                }}
                                                className={isTotalRow ? "greyRow" : ""}
                                            >
                                                <td
                                                    rowSpan={reportData.displayProductionCol ? 4 : 3}
                                                    className="center"
                                                    style={{ borderLeft: "solid 1px #aaa" }}
                                                >
                                                    {isTotalRow ? "Team Total" : x.rowLabel}
                                                </td>
                                                <td>Walkthrough</td>
                                                <td className="right">{x.pendingGeneralWalkThrough}</td>
                                                <td className="right">{x.pendingRIFWalkThrough}</td>
                                                <td className="right">{x.pendingCorrespondWalkThrough}</td>
                                                <td className="right">{x.pendingEscalatedWalkThrough}</td>
                                                <td className="right">{x.completedRespondedWalkThrough}</td>
                                                <td className="right">{x.completedNoResponseWalkThrough}</td>
                                                <td className="right">{x.completedDelegateWalkThrough}</td>
                                                <td className="right">{x.completedReassignedWalkThrough}</td>
                                                <td className="right">{x.totalWalkThrough}</td>
                                                {reportData.displayProductionCol && (
                                                    <td style={{ borderRight: "0px" }}>Prod</td>
                                                )}
                                                {reportData.displayProductionCol && (
                                                    <td
                                                        className="right"
                                                        style={{ borderLeft: "0px" }}
                                                    >
                                                        {x.productionProd}
                                                    </td>
                                                )}
                                            </tr>
                                            <tr
                                                style={{ background: index % 2 === 0 ? "#fff" : "#f3f3f3" }}
                                                className={isTotalRow ? "greyRow" : ""}
                                            >
                                                <td>Work Items</td>
                                                <td className="right">{x.pendingGeneralWorkItems}</td>
                                                <td className="right">{x.pendingRIFWorkItems}</td>
                                                <td className="right">{x.pendingCorrespondWorkItems}</td>
                                                <td className="right">{x.pendingEscalatedWorkItems}</td>
                                                <td className="right">{x.completedRespondedWorkItems}</td>
                                                <td className="right">{x.completedNoResponseWorkItems}</td>
                                                <td className="right">{x.completedDelegateWorkItems}</td>
                                                <td className="right">{x.completedReassignedWorkItems}</td>
                                                <td className="right">{x.totalWorkItems}</td>
                                                {reportData.displayProductionCol && (
                                                    <td style={{ borderRight: "0px" }}>Shift</td>
                                                )}
                                                {reportData.displayProductionCol && (
                                                    <td
                                                        className="right"
                                                        style={{ borderLeft: "0px" }}
                                                    >
                                                        {x.productionShift}
                                                    </td>
                                                )}
                                            </tr>
                                            <tr
                                                style={{ background: index % 2 === 0 ? "#fff" : "#f3f3f3" }}
                                                className={isTotalRow ? "greyRow" : ""}
                                            >
                                                <td>Average Time</td>
                                                <td className="right">
                                                    {ConvertSecondsToHHMMSS(x.pendingGeneralAverageTime)}
                                                </td>
                                                <td className="right">
                                                    {ConvertSecondsToHHMMSS(x.pendingRIFAverageTime)}
                                                </td>
                                                <td className="right">
                                                    {ConvertSecondsToHHMMSS(x.pendingCorrespondAverageTime)}
                                                </td>
                                                <td className="right">
                                                    {ConvertSecondsToHHMMSS(x.pendingEscalatedAverageTime)}
                                                </td>
                                                <td className="right">
                                                    {ConvertSecondsToHHMMSS(x.completedRespondedAverageTime)}
                                                </td>
                                                <td className="right">
                                                    {ConvertSecondsToHHMMSS(x.completedNoResponseAverageTime)}
                                                </td>
                                                <td className="right">
                                                    {ConvertSecondsToHHMMSS(x.completedDelegateAverageTime)}
                                                </td>
                                                <td className="right">
                                                    {ConvertSecondsToHHMMSS(x.completedReassignedAverageTime)}
                                                </td>
                                                <td className="right">{ConvertSecondsToHHMMSS(x.totalAverageTime)}</td>
                                                {reportData.displayProductionCol && (
                                                    <td style={{ borderRight: "0px" }}>Down</td>
                                                )}
                                                {reportData.displayProductionCol && (
                                                    <td
                                                        className="right"
                                                        style={{ borderLeft: "0px" }}
                                                    >
                                                        {x.productionDown}
                                                    </td>
                                                )}
                                            </tr>
                                            {reportData.displayProductionCol && (
                                                <tr
                                                    style={{
                                                        background: index % 2 === 0 ? "#fff" : "#f3f3f3",
                                                    }}
                                                    className={isTotalRow ? "greyRow" : ""}
                                                >
                                                    <td>Production</td>
                                                    <td className="right">
                                                        {ConvertSecondsToHHMMSS(x.pendingGeneralProduction)}
                                                    </td>
                                                    <td className="right">
                                                        {ConvertSecondsToHHMMSS(x.pendingRIFProduction)}
                                                    </td>
                                                    <td className="right">
                                                        {ConvertSecondsToHHMMSS(x.pendingCorrespondProduction)}
                                                    </td>
                                                    <td className="right">
                                                        {ConvertSecondsToHHMMSS(x.pendingEscalatedProduction)}
                                                    </td>
                                                    <td className="right">
                                                        {ConvertSecondsToHHMMSS(x.completedRespondedProduction)}
                                                    </td>
                                                    <td className="right">
                                                        {ConvertSecondsToHHMMSS(x.completedNoResponseProduction)}
                                                    </td>
                                                    <td className="right">
                                                        {ConvertSecondsToHHMMSS(x.completedDelegateProduction)}
                                                    </td>
                                                    <td className="right">
                                                        {ConvertSecondsToHHMMSS(x.completedReassignedProduction)}
                                                    </td>
                                                    <td className="right">
                                                        {ConvertSecondsToHHMMSS(x.totalProduction)}
                                                    </td>
                                                    <td style={{ borderRight: "0px" }}>Prod %</td>
                                                    <td
                                                        className="right"
                                                        style={{ borderLeft: "0px" }}
                                                    >
                                                        {x.productionProdPercentage}
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    );
                                })}

                                {
                                    //user end
                                }
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </>
    );
}
export default UserProductivityReport;
